import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/client";
import ProductCard from "../components/ProductCard";
import { ProductGridSkeleton } from "../components/Skeletons";
import { SlidersHorizontal, X } from "lucide-react";

const FLAVORS = [
    { value: "all", label: "All" },
    { value: "classic", label: "Classic" },
    { value: "peri_peri", label: "Peri Peri" },
    { value: "pudina", label: "Pudina" },
    { value: "chocolate", label: "Chocolate" },
];
const PACKS = [
    { value: "all", label: "All sizes" },
    { value: "50g", label: "50g" },
    { value: "100g", label: "100g" },
    { value: "200g", label: "200g" },
];
const SORTS = [
    { value: "popularity", label: "Popular" },
    { value: "price_asc", label: "Price: low to high" },
    { value: "price_desc", label: "Price: high to low" },
    { value: "rating", label: "Top rated" },
];

export default function Products() {
    const [sp, setSp] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const flavor = sp.get("flavor") || "all";
    const pack = sp.get("pack_size") || "all";
    const sort = sp.get("sort") || "popularity";
    const search = sp.get("search") || "";
    const minPrice = sp.get("min_price") || "";
    const maxPrice = sp.get("max_price") || "";

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const params = { sort };
                if (flavor !== "all") params.flavor = flavor;
                if (pack !== "all") params.pack_size = pack;
                if (minPrice) params.min_price = minPrice;
                if (maxPrice) params.max_price = maxPrice;
                if (search) params.search = search;
                const { data } = await api.get("/products", { params });
                setProducts(data);
            } finally {
                setLoading(false);
            }
        })();
    }, [flavor, pack, sort, minPrice, maxPrice, search]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(sp);
        if (!value || value === "all") next.delete(key);
        else next.set(key, value);
        setSp(next);
    };

    const activeFilters = useMemo(() => {
        const c = [];
        if (flavor !== "all") c.push({ key: "flavor", label: FLAVORS.find(f => f.value === flavor)?.label });
        if (pack !== "all") c.push({ key: "pack_size", label: pack });
        if (search) c.push({ key: "search", label: `"${search}"` });
        if (minPrice || maxPrice) c.push({ key: "price", label: `₹${minPrice || 0} - ₹${maxPrice || "∞"}` });
        return c;
    }, [flavor, pack, search, minPrice, maxPrice]);

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 animate-fade-slide">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                <div>
                    <div className="text-xs font-black uppercase tracking-[0.25em] text-brand-orange mb-3">
                        Shop
                    </div>
                    <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter">All makhana</h1>
                    {search && <p className="text-brand-moss mt-2">Search results for "{search}"</p>}
                </div>
                <div className="flex items-center gap-3">
                    <select
                        value={sort}
                        onChange={(e) => setParam("sort", e.target.value)}
                        data-testid="sort-select"
                        className="bg-white border border-brand-line rounded-xl px-4 py-3 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-brand-orange"
                    >
                        {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <button
                        onClick={() => setFiltersOpen(true)}
                        data-testid="open-filters-btn"
                        className="lg:hidden bg-brand-ink text-white px-4 py-3 rounded-xl text-sm font-bold flex items-center gap-2"
                    >
                        <SlidersHorizontal size={16} /> Filters
                    </button>
                </div>
            </div>

            {/* Flavor pills */}
            <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-4 mb-6">
                {FLAVORS.map((f) => (
                    <button
                        key={f.value}
                        data-testid={`flavor-pill-${f.value}`}
                        onClick={() => setParam("flavor", f.value)}
                        className={`whitespace-nowrap px-5 py-2 rounded-full border text-sm font-bold transition ${
                            flavor === f.value
                                ? "bg-brand-ink text-white border-brand-ink"
                                : "bg-white border-brand-line hover:border-brand-orange"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {activeFilters.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-6">
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-moss">Active:</span>
                    {activeFilters.map((f, i) => (
                        <button
                            key={i}
                            onClick={() => {
                                if (f.key === "price") {
                                    setParam("min_price", "");
                                    setParam("max_price", "");
                                } else setParam(f.key, "");
                            }}
                            className="inline-flex items-center gap-1.5 bg-brand-sand text-brand-ink px-3 py-1.5 rounded-full text-xs font-bold"
                        >
                            {f.label} <X size={12} />
                        </button>
                    ))}
                </div>
            )}

            <div className="grid lg:grid-cols-[260px,1fr] gap-10">
                {/* Desktop filters */}
                <aside className="hidden lg:block space-y-8 sticky top-28 self-start">
                    <FilterPanel
                        flavor={flavor} pack={pack} minPrice={minPrice} maxPrice={maxPrice}
                        setParam={setParam}
                    />
                </aside>

                <div>
                    {loading ? (
                        <ProductGridSkeleton count={6} />
                    ) : products.length === 0 ? (
                        <div data-testid="empty-products" className="bg-white border border-brand-line rounded-2xl p-12 text-center">
                            <h3 className="font-heading font-bold text-2xl mb-2">No products found</h3>
                            <p className="text-brand-moss">Try changing your filters.</p>
                        </div>
                    ) : (
                        <div
                            data-testid="product-grid"
                            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
                        >
                            {products.map((p) => <ProductCard key={p.id} product={p} />)}
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile filters sheet */}
            {filtersOpen && (
                <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setFiltersOpen(false)}>
                    <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-heading font-black text-2xl">Filters</h3>
                            <button onClick={() => setFiltersOpen(false)}><X /></button>
                        </div>
                        <FilterPanel
                            flavor={flavor} pack={pack} minPrice={minPrice} maxPrice={maxPrice}
                            setParam={setParam}
                        />
                        <button
                            onClick={() => setFiltersOpen(false)}
                            className="w-full mt-6 bg-brand-orange text-white font-bold py-4 rounded-xl"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function FilterPanel({ flavor, pack, minPrice, maxPrice, setParam }) {
    return (
        <>
            <div>
                <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-brand-moss mb-3">Flavor</h4>
                <div className="space-y-2">
                    {FLAVORS.map((f) => (
                        <button
                            key={f.value}
                            onClick={() => setParam("flavor", f.value)}
                            className={`block w-full text-left px-4 py-2 rounded-lg text-sm font-bold transition ${
                                flavor === f.value ? "bg-brand-ink text-white" : "hover:bg-brand-sand"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-brand-moss mb-3">Pack size</h4>
                <div className="space-y-2">
                    {PACKS.map((p) => (
                        <button
                            key={p.value}
                            onClick={() => setParam("pack_size", p.value)}
                            className={`block w-full text-left px-4 py-2 rounded-lg text-sm font-bold transition ${
                                pack === p.value ? "bg-brand-ink text-white" : "hover:bg-brand-sand"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-brand-moss mb-3">Price range</h4>
                <div className="flex gap-2">
                    <input
                        type="number"
                        placeholder="Min"
                        value={minPrice}
                        onChange={(e) => setParam("min_price", e.target.value)}
                        data-testid="price-min-input"
                        className="w-full bg-brand-cream border border-brand-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"
                    />
                    <input
                        type="number"
                        placeholder="Max"
                        value={maxPrice}
                        onChange={(e) => setParam("max_price", e.target.value)}
                        data-testid="price-max-input"
                        className="w-full bg-brand-cream border border-brand-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"
                    />
                </div>
            </div>
        </>
    );
}
